import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Button, Form, Input, Modal } from "./admin-ui";

type OrderCancelModalProps = {
  open: boolean;
  loading?: boolean;
  orderCode?: string | null;
  onCancel: () => void;
  onConfirm: (values: { reason: string }) => void;
};

export function OrderCancelModal({ open, loading, orderCode, onCancel, onConfirm }: OrderCancelModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const close = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      title={
        <div className="rounded-2xl border border-[#eee8e2] bg-white p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#fff7ed] text-[#ea580c]">
                <AlertTriangle size={19} strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-lg font-black leading-6 text-[#191c1d]">{t("orders.cancelModalTitle")}</div>
                <div className="mt-1 max-w-[420px] text-sm font-medium leading-5 text-[#867461]">
                  {t("orders.cancelHint")}
                </div>
              </div>
            </div>
            {orderCode ? (
              <span className="shrink-0 rounded-full bg-[#f3f4f5] px-3 py-1 text-xs font-black uppercase tracking-[0.5px] text-[#006c49]">
                #{orderCode}
              </span>
            ) : null}
          </div>
        </div>
      }
      footer={
        <>
          <Button
            onClick={close}
            className="h-11 min-w-[104px] rounded-xl !border-[#e7e1db] !bg-white px-5 text-sm font-extrabold !text-[#534434] hover:!border-[#fed7aa] hover:!bg-[#fff7ed] hover:!text-[#ea580c]"
          >
            {t("common.back")}
          </Button>
          <Button
            loading={loading}
            onClick={() => void form.submit()}
            className="h-11 min-w-[132px] rounded-xl !border-transparent !bg-[#dc2626] px-5 text-sm font-extrabold !text-white shadow-[0_8px_18px_rgba(220,38,38,0.16)] hover:!border-transparent hover:!bg-[#b91c1c] hover:!text-white"
          >
            {t("orders.cancelConfirm")}
          </Button>
        </>
      }
    >
      <div className="mt-4 rounded-2xl border border-[#eee8e2] bg-[#fffaf5] p-4">
        <Form form={form} layout="vertical" onFinish={onConfirm}>
          <Form.Item
            name="reason"
            label={<span className="text-xs font-black uppercase tracking-[0.08em] text-[#534434]">{t("orders.cancelReason")}</span>}
            rules={[{ required: true, min: 3, max: 500 }]}
          >
            <Input.TextArea
              rows={4}
              maxLength={500}
              showCount
              placeholder={t("orders.cancelReasonPlaceholder")}
              className="min-h-[112px] rounded-xl border border-[#e7e1db] bg-white p-3 text-sm font-medium leading-6 text-[#191c1d] outline-none placeholder:text-[#a99a8b] focus:border-[#ea580c] focus:ring-4 focus:ring-[#ea580c]/10"
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}
